import { FilterTypes } from "../../../handlers/filter";
import { isDescending } from "../../../handlers/sort";
/**
 * Mirrors `ListProposalsRequest.page_size`, which documents `0` as defaulting
 * to 100 server-side.
 */
export const DEFAULT_PAGE_SIZE = 100;
/**
 * The most rows `ListProposalsRequest.page_size` accepts — larger values are
 * rejected with InvalidArgument by protovalidate before the handler runs.
 */
export const MAX_PAGE_SIZE = 500;
/**
 * Builds the `order_by` value. The endpoint takes a bare field for ascending
 * and a `" desc"` suffix for descending — anything else is rejected with
 * InvalidArgument.
 */
export const buildSort = (sort) => isDescending(sort) ? `${sort.field} desc` : sort.field;
/**
 * Translates the SDK's `Filter<ProposalFilters>` shape into the query string the
 * seller service's ListProposals endpoint expects.
 *
 * Only equality filters are forwarded. A string value emits one param; an array
 * value (e.g. `products`) emits one repeated param per entry
 * (`products=CTV&products=OLV`), which is how the endpoint's array query params
 * are read. Non-EQ filter types and other value shapes are silently dropped,
 * since this translation has no way to express them — surfacing an error here
 * would be surprising to callers who pass through generic filter shapes from
 * higher up.
 *
 * Paging is emitted only when `filters.paging` is set. Unlike most Madhive
 * endpoints, omitting `page_size` here does not return the full set — the
 * server falls back to 100 — so callers that need everything have to walk
 * `next_page_token`.
 */
export const toQueryParams = (filters, sort) => {
    // A list of pairs rather than an object so array-valued filters can emit the
    // same key more than once (repeated query params).
    const params = [];
    for (const where of filters?.where ?? []) {
        if (where.type !== FilterTypes.EQ)
            continue;
        if (typeof where.value === "string") {
            params.push([where.field, where.value]);
        }
        else if (Array.isArray(where.value)) {
            // Repeated param per entry — the endpoint reads array query params like
            // `products` as one occurrence per value. Non-string entries are skipped.
            for (const entry of where.value) {
                if (typeof entry === "string")
                    params.push([where.field, entry]);
            }
        }
    }
    if (filters?.paging) {
        params.push(["page_size", String(filters.paging.size || DEFAULT_PAGE_SIZE)]);
        if (filters.paging.token) {
            // The token encodes an offset server-side, so it is replayed verbatim.
            params.push(["page_token", filters.paging.token]);
        }
    }
    if (sort?.field && sort.direction) {
        params.push(["order_by", buildSort(sort)]);
    }
    const encoded = params
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join("&");
    return encoded ? `?${encoded}` : "";
};
//# sourceMappingURL=utils.js.map